import { Button } from '@flexo/atoms';
import { IconWrapper } from '@flexo/general';
import {
  DetailsListConfig,
  DetailsListType,
  useDetailsListData,
} from '@flexo/hooks';
import { ALERT, alertCallbackStore } from '@flexo/reducers';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
// import Accordion from '@mui/material/Accordion';
// import AccordionSummary from '@mui/material/AccordionSummary';
// import AccordionDetails from '@mui/material/AccordionDetails';

import styles from './details-list.module.scss';

const ENV = (import.meta as any).env;

export interface DetailsListProps {
  type: DetailsListType;
  data?: any;
}

export function DetailsList({ type, data = null }: DetailsListProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.user);
  const metadata = useSelector((state: any) => state.metadata);

  const {
    isGroup,
    expanded,
    setExpanded,
    singleConfig,
    groupConfigs,
    collapsedPreviewCount,
    totalItems,
    hasExpandCollapse,
    shouldScrollGroup,
  } = useDetailsListData(type, data);

  // Helpers
  // eslint-disable-next-line
  const emailRegex =
    /^(?:[a-zA-Z0-9_'^&+-])+(?:\.(?:[a-zA-Z0-9_'^&+-])+)*@(?:(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}|\[(?:(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\])$/;

  function isValidEmail(value: unknown): boolean {
    return typeof value === 'string' && emailRegex.test(value);
  }

  function copyToClipboard(text: string) {
    if (!text) return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(() => void 0);
      return;
    }
    try {
      const el = document.createElement('textarea');
      el.value = text;
      el.setAttribute('readonly', '');
      el.style.position = 'absolute';
      el.style.left = '-9999px';
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    } catch {
      // no-op
    }
  }

  // UI rendering function for individual details list items
  function renderDetailsList(config: DetailsListConfig) {
    const {
      title,
      isClickable = false,
      subtitle,
      dataList,
      onTitleClick,
      isDocked = false,
    } = config;

    const {
      Details__Wrapper__Compact,
      Details__Paper,
      Details__Title,
      Details__Title__Clickable,
      Details__Subtitle,
      Details__Content,
      Details__Content__Name,
      Details__Content__Suffix,
    } = styles;

    const handleTitleClick = () => {
      if (isClickable && onTitleClick) {
        onTitleClick();
      }
    };

    return (
      <div key={uuidv4()} className={Details__Wrapper__Compact}>
        <div className={Details__Paper}>
          {isDocked === false && (
            <>
              {isClickable ? (
                <p
                  onClick={handleTitleClick}
                  className={`heading4 ${Details__Title} ${Details__Title__Clickable}`}
                >
                  {title}
                  <IconWrapper iconName="chevron-right" />
                </p>
              ) : (
                <p className={Details__Title}>{title}</p>
              )}
            </>
          )}

          {subtitle && <p className={Details__Subtitle}>{subtitle}</p>}

          <div>
            {dataList.map((item, index) => {
              const displayValue = item.value || '-';
              const email = isValidEmail(displayValue);
              return (
                <div
                  key={`${item.title}_${index}_${uuidv4()}`}
                  className="flex flex-column"
                >
                  <span
                    className={Details__Content}
                    onClick={
                      email ? () => copyToClipboard(displayValue) : undefined
                    }
                    style={email ? { cursor: 'pointer' } : undefined}
                  >
                    {displayValue}
                    {item.valueSuffix && (
                      <span className={Details__Content__Suffix}>
                        {' '}
                        {item.valueSuffix}
                      </span>
                    )}
                  </span>
                  <span
                    className={Details__Content__Name}
                    style={{
                      marginBottom:
                        index === dataList.length - 1 ? 0 : undefined,
                    }}
                  >
                    {item.title}
                  </span>
                </div>
              );
            })}
          </div>

          {type === 'memberDetails' && ENV.VITE_APP_SHOW_CLERK_MEMBER_INFO !== 'false' &&
            (() => {
              const selectedEntity = metadata?.selectedEntity as any;
              const organizationId =
                user?.organizationId || ENV.VITE_APP_ORG_ID;
              const isAdmin = user?.orgRole === 'org:admin';
              const isSelf = selectedEntity?.clerkUserId === user?.uid;
              const hasAppAccount = selectedEntity?.hasAppAccount === true;

              // Only offer deletion when there is an actual app (Clerk) account to delete
              if (!isAdmin || !hasAppAccount || isSelf || selectedEntity?.disabled)
                return null;

              return (
                <div className={styles.Details__DeleteButton__Wrapper}>
                  <button
                    className={styles.Details__DeleteButton}
                    onClick={() => {
                      alertCallbackStore.setData({
                        userId: selectedEntity.clerkUserId || selectedEntity.memberId,
                        organizationId,
                      });
                      dispatch(
                        ALERT.SetAlert({
                          value: { type: 'ALERT_REMOVE_MEMBER_APP_ACCOUNT' },
                        } as any)
                      );
                    }}
                  >
                    {t('widgets.memberDetailsParameters.deleteAppAccount')}
                  </button>
                </div>
              );
            })()}
        </div>
      </div>
    );
  }

  // Render single item
  function renderSingle(isDocked = false) {
    if (!singleConfig) return null;
    return renderDetailsList({
      ...singleConfig,
      isDocked: isDocked,
    });
  }

  // Render group of items
  function renderGroup() {
    const {
      Sites__List__Wrapper,
      Sites__List__Wrapper__Expanded,
      Sites__List__ItemWrapper,
      Sites__List__Expand__Button__Wrapper,
    } = styles;
    return (
      <div
        key={`${type}_list_${uuidv4()}`}
        className={`${Sites__List__Wrapper} ${
          expanded && Sites__List__Wrapper__Expanded
        }`}
        data-details-list-type={type}
        data-total-items={totalItems}
        data-overflow={shouldScrollGroup ? 'true' : 'false'}
        data-expanded={expanded ? 'true' : 'false'}
      >
        {groupConfigs.map((config, index) => {
          if (hasExpandCollapse && !expanded && index >= collapsedPreviewCount) {
            return null;
          }

          const isInactive = config.isInactive;

          return (
            <div
              key={`item_${index}_${uuidv4()}`}
              className={Sites__List__ItemWrapper}
              data-group-item="true"
              style={{ opacity: isInactive ? 0.5 : 1 }}
            >
              {renderDetailsList(config)}
            </div>
          );
        })}

        {hasExpandCollapse && (
          <div className={Sites__List__Expand__Button__Wrapper}>
            <Button
              className={styles.Sites__List__Expand__Button}
              variant="primary"
              color="secondary"
              size="small"
              label={
                type === 'siteMembers'
                  ? expanded
                    ? t('widgets.siteMembersList.collapse')
                    : t('widgets.siteMembersList.expand', {
                        membersNumber: totalItems,
                      })
                  : expanded
                  ? t('widgets.memberSitesDetails.collapse')
                  : t('widgets.memberSitesDetails.expand', {
                      sitesNumber: totalItems,
                    })
              }
              onClick={() => setExpanded(!expanded)}
            />
          </div>
        )}
      </div>
    );
  }

  return isGroup ? renderGroup() : renderSingle();

  // if ( window.innerWidth < 991 ) {
  //   return <Accordion
  //       className={styles.Accordion}
  //       onChange={(_e, expanded) => setIsExpanded(expanded)}
  //     >
  //       <AccordionSummary
  //         aria-controls="panel1-content"
  //         id="panel1-header"
  //         className={styles.Accordion__Summary}

  //       >
  //         <div className={styles.Accordion__Summary__Title__Wrapper}>
  //           <p>
  //             {(() => {
  //               switch ( type ) {
  //                 case 'memberSites':
  //                   return t('widgets.memberSitesDetails.siteDetails');
  //                 case 'memberDetails':
  //                   return t('widgets.memberDetailsParameters.title');
  //                 case 'siteDetails':
  //                   return t('widgets.siteDetailsParameters.title');
  //                 case 'communityDetails':
  //                   return t('widgets.communityDetailsParameters.title');
  //                 case 'siteMembers':
  //                   return t('widgets.memberDetailsParameters.title');
  //                 default:
  //                   return '';
  //               }
  //             } )()}
  //               <IconWrapper
  //                 className={`${styles.Accordion__Summary__Icon} ${isExpanded ? 'rotate-90' : 'rotato-0'}`}
  //                 iconName={'chevron-right'}
  //               />
  //           </p>
  //           {(() => {
  //           if (isExpanded) { return ''; }

  //               switch ( type ) {
  //                 case 'memberSites':
  //                   case 'siteMembers':
  //                   return <p> {groupConfigs[0]?.title}</p>;
  //                 case 'memberDetails':
  //                 case 'siteDetails':
  //                   return <p>{ singleConfig?.subtitle}</p>;
  //                 case 'communityDetails':
  //                   return <p>{ singleConfig?.dataList?.[3]?.value || ''}</p>;
  //                 default:
  //                   return '';
  //               }
  //             } )()}
  //         </div>

  //       </AccordionSummary>
  //       <AccordionDetails>
  //         {isGroup ? renderGroup(true) : renderSingle(true)}
  //       </AccordionDetails>
  //     </Accordion>
  // } else {
  //   return isGroup ? renderGroup() : renderSingle();
  // }
}

export default DetailsList;
